import React, { useState, useEffect } from "react";
import { Button, Divider, List, Tooltip } from "antd";
import RestoreIcon from "./icons/restore";
import { useUserGuide } from "./user-guide-context.jsx";
import { convertMillisecondsToDateTime } from "./helper/helper.js";

export default function RestoreVersion({ onConfirmClick, publish_version = [] }) {
  const [dataSource, setDataSource] = useState([]);
  const { state } = useUserGuide();
  const selectedItem = state.selectedItem;

  useEffect(() => {
    // Convert publish_version (object or array) to dataSource format
    let versions = [];
    
    // Debug: Log the publish_version object to see its structure
    console.log("RestoreVersion - publish_version received:", JSON.stringify(publish_version, null, 2));
    
    if (publish_version) {
      if (typeof publish_version === 'object' && !Array.isArray(publish_version)) {
        // Handle object structure: { "1.0.0": "draft", "1.0.1": "published" }
        versions = Object.keys(publish_version).sort((a, b) => {
          // Sort versions numerically (descending - latest first)
          const aParts = a.split('.').map(Number);
          const bParts = b.split('.').map(Number);
          for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
            const aVal = aParts[i] || 0;
            const bVal = bParts[i] || 0;
            if (aVal !== bVal) return bVal - aVal; // Descending order
          }
          return 0;
        });
      } else if (Array.isArray(publish_version) && publish_version.length > 0) {
        // Fallback for array (backward compatibility)
        versions = [...publish_version].reverse();
      }
    }
    
    if (versions.length > 0) {
      const versionsData = versions.map((version, index) => {
        // Get published_at from publish_version if available (new format)
        const versionData = typeof publish_version === 'object' && !Array.isArray(publish_version) 
          ? publish_version[version] 
          : null;
        
        let lastModified = null;
        let publishedAtString = null;
        
        // If versionData is an object with published_at, parse it
        if (versionData && typeof versionData === 'object' && versionData.published_at) {
          publishedAtString = versionData.published_at;
          try {
            // Convert "2025-12-24 05:02:34" to ISO format and then to timestamp
            // The API format is "YYYY-MM-DD HH:mm:ss"
            // We need to parse it correctly - the API likely provides it in UTC
            const dateStr = versionData.published_at.replace(' ', 'T');
            
            // Parse as UTC (add 'Z' to indicate UTC)
            const date = new Date(dateStr + 'Z');
            
            if (!isNaN(date.getTime())) {
              lastModified = date.getTime();
              console.log("Parsed date for version", version, ":", {
                original: versionData.published_at,
                parsedUTC: date.toISOString(),
                timestamp: lastModified
              });
            } else {
              console.warn("Invalid date format for version", version, ":", versionData.published_at);
            }
          } catch (e) {
            console.warn("Error parsing published_at for version", version, ":", e, versionData.published_at);
          }
        } else if (typeof versionData === 'string') {
          // Old format - just status string, no date
          publishedAtString = null;
        }
        
        return {
          id: index + 1,
          version: version,
          lastModified: lastModified,
          published_at: publishedAtString,
          status: versionData && typeof versionData === 'object' ? versionData.status : (typeof versionData === 'string' ? versionData : null),
        };
      });
      setDataSource(versionsData);
    } else {
      setDataSource([]);
    }
  }, [publish_version, selectedItem?.id]);

  // Use IST (Indian Standard Time) for restore version modal
  const IST_TIMEZONE = "Asia/Kolkata";

  const formatDateTime = (dateToFormat) => {
    return convertMillisecondsToDateTime(dateToFormat, IST_TIMEZONE);
  };

  return (
    <>
      <List
        style={{ maxHeight: "300px", overflowY: "auto", height: "200px" }}
        itemLayout="horizontal"
        dataSource={dataSource}
        renderItem={(param) => (
          <List.Item
            style={{
              // padding: "0px 24px",
              borderBottom: "none",
            }}
          >
            <List.Item.Meta
              style={{ padding: "0px" }}
              title={
                <>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      padding: "0px",
                    }}
                  >
                    <div>
                      {param?.version?.length > 20 ? (
                        <Tooltip title={param?.version}>
                          <h3 style={{fontWeight: "700", fontSize: "16px"}}>
                          Version:{param?.version?.slice(0, 20) + "..."} - <span style={{color: "grey",fontWeight: "400",fontSize:"14px"}}>{param?.status==='publish'?"Published":"Draft"}</span>
                          </h3>
                        </Tooltip>
                      ) : (
                        <h3 style={{fontWeight: "700", fontSize: "16px"}}>Version:{param?.version} - <span style={{color: "grey",fontWeight: "400",fontSize:"14px"}}>{param?.status==='publish'?"Published":"Draft"}</span></h3>
                      )}
                      {param?.published_at && param?.lastModified ? (
                        <p style={{fontWeight: "400", fontSize: "12px", color:'#64748B', marginTop: "4px"}}>
                          {formatDateTime(param.lastModified)}
                        </p>
                      ) : param?.status === "draft" ? (
                        <p style={{fontWeight: "400", fontSize: "12px", color:'#64748B', marginTop: "4px"}}>
                          Draft
                        </p>
                      ) : null}
                    </div>
                    <div style={{ marginTop: "0px" }}>
                      <Tooltip title="Restore">
                        <Button
                          style={{border: "none", padding: "0px", margin: "0px"}}
                          type="ghost"
                          icon={
                            <div
                              style={{ paddingTop: "2px" }}
                              onClick={() => onConfirmClick(param)}
                            >
                              <RestoreIcon color="#64748B" />
                            </div>
                          }
                        />
                      </Tooltip>
                    </div>
                  </div>
                  <Divider style={{ margin: "0px" }} />
                </>
              }
            />
          </List.Item>
        )}
      />
    </>
  );
}
